export { ImpactAssessmentChecklistForm } from "./ImpactAssessmentChecklistForm";
export type { ImpactAssessmentChecklistFormProps } from "./ImpactAssessmentChecklistForm";
export { ConceptsSelector } from "./ConceptsSelector";
export type {
  ConceptsSelectorProps,
  ConceptsSelectorValue,
  ConceptsSelectorVariant,
} from "./ConceptsSelector";
export { DEFAULT_CONCEPTS_LIST, CONCEPT_CATEGORIES } from "./constants";
export type {
  ChecklistConceptItem,
  ConceptCategory,
  ImpactAssessmentChecklistFormValue,
  ConceptCodeMapping,
} from "./types";

import type {
  ChecklistConceptItem,
  ImpactAssessmentChecklistFormValue,
  ConceptCodeMapping,
} from "./types";
import type { AssessmentChecklistItem } from "../../services/assessmentService";

/** Map dimension display name to API concept_type */
const DIMENSION_TO_CONCEPT_TYPE: Record<string, string> = {
  "Product Type": "ProductType",
  Department: "Department",
  "Therapeutic Area": "TherapeuticArea",
  Ingredient: "Ingredient",
  All: "All",
};

/** Map API concept_type to dimension display name */
const CONCEPT_TYPE_TO_DIMENSION: Record<string, string> = {
  ProductType: "Product Type",
  Department: "Department",
  TherapeuticArea: "Therapeutic Area",
  Ingredient: "Ingredient",
};

/** Map dimension to API concept name (for lookup in conceptCodeMapping from values/search) */
const DIMENSION_TO_CONCEPT_NAME: Record<string, string> = {
  "Product Type": "ProductType",
  Department: "Department",
  "Therapeutic Area": "TherapeuticArea",
  Ingredient: "IngredientName",
};

/** Value from API may use code or id; name or value for display */
function getConceptValueId(v: { code?: string; id?: string; name?: string; value?: string }): string {
  return v.code ?? v.id ?? String(v.name ?? v.value ?? "");
}

function getConceptValueName(v: { code?: string; id?: string; name?: string; value?: string }): string {
  return v.name ?? v.value ?? getConceptValueId(v);
}

/**
 * Convert API checklist item to form value for editing.
 * Handles both { code, name } and { id, name } / { id, value } value shapes so
 * Selected Concepts are restored when the list API returns full concept details.
 */
export function assessmentChecklistToFormValue(
  item: AssessmentChecklistItem
): ImpactAssessmentChecklistFormValue {
  const concepts = item.concepts?.concepts ?? [];
  const selectedConceptsDetails: ChecklistConceptItem[] = [];
  const selectedConceptIds: string[] = [];

  for (const c of concepts) {
    const dimension =
      CONCEPT_TYPE_TO_DIMENSION[c.concept_type] ??
      c.concept_type.replace(/([A-Z])/g, " $1").trim();
    const values = c.values ?? [];
    for (const v of values) {
      const valueId = getConceptValueId(v as { code?: string; id?: string; name?: string; value?: string });
      const valueName = getConceptValueName(v as { code?: string; id?: string; name?: string; value?: string });
      if (!valueId) continue;
      selectedConceptIds.push(valueId);
      selectedConceptsDetails.push({
        id: valueId,
        dimension,
        name: valueName,
        concept_code: c.concept_code,
      });
    }
  }

  return {
    name: item.name,
    description: item.description ?? "",
    selectedConceptIds,
    selectedConceptsDetails,
    keywords: Array.isArray(item.keywords) ? item.keywords.join(", ") : "",
  };
}

/** Normalize dimension for grouping so "ProductType" and "Product Type" merge into one */
function normalizeDimensionForGrouping(dim: string): string {
  const d = dim?.trim() ?? "";
  const mapping: Record<string, string> = {
    ProductType: "Product Type",
    "Product Type": "Product Type",
    TherapeuticArea: "Therapeutic Area",
    "Therapeutic Area": "Therapeutic Area",
    IngredientName: "Ingredient",
    Ingredient: "Ingredient",
    Department: "Department",
  };
  return mapping[d] ?? d;
}

/**
 * Convert checklist form value to API create payload format.
 * @param value - Form value
 * @param conceptCodeMapping - Optional mapping from API concept name to concept_code (from values/search). Used when items lack concept_code.
 */
export function checklistFormValueToCreatePayload(
  value: ImpactAssessmentChecklistFormValue,
  conceptCodeMapping?: ConceptCodeMapping
): {
  name: string;
  description: string;
  concepts: {
    concepts: Array<{
      concept_type: string;
      concept_code: string;
      values: Array<{ code: string; name: string }>;
    }>;
  };
  keywords: string[];
  is_favorite: boolean;
} {
  const details = value.selectedConceptsDetails ?? [];
  // Group by dimension (same concept type = one group)
  const byGroup = new Map<string, ChecklistConceptItem[]>();
  for (const c of details) {
    const key = normalizeDimensionForGrouping(c.dimension ?? "Other");
    const list = byGroup.get(key) ?? [];
    list.push(c);
    byGroup.set(key, list);
  }
  const concepts = Array.from(byGroup.entries()).map(([dimKey, items]) => {
    const conceptType = DIMENSION_TO_CONCEPT_TYPE[dimKey] ?? dimKey;
    const apiConceptName = DIMENSION_TO_CONCEPT_NAME[dimKey];
    const conceptCode =
      (conceptCodeMapping && apiConceptName
        ? conceptCodeMapping[apiConceptName]
        : undefined) ??
      items.find((c) => c.concept_code?.startsWith("GCC-"))?.concept_code ??
      items[0]?.concept_code ??
      conceptType;
    return {
      concept_type: conceptType,
      concept_code: conceptCode,
      values: items.map((c) => ({ code: c.id, name: c.name })),
    };
  });
  const keywords = value.keywords
    .trim()
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    name: value.name.trim(),
    description: value.description.trim(),
    concepts: { concepts },
    keywords,
    is_favorite: false,
  };
}

/**
 * Convert checklist form value to API update payload format.
 * Same as create but with optional is_favorite.
 */
export function checklistFormValueToUpdatePayload(
  value: ImpactAssessmentChecklistFormValue,
  is_favorite?: boolean,
  conceptCodeMapping?: ConceptCodeMapping
): {
  name: string;
  description: string;
  concepts: {
    concepts: Array<{
      concept_type: string;
      concept_code: string;
      values: Array<{ code: string; name: string }>;
    }>;
  };
  keywords: string[];
  is_favorite?: boolean;
} {
  const { concepts, ...rest } = checklistFormValueToCreatePayload(
    value,
    conceptCodeMapping
  );
  return {
    ...rest,
    concepts,
    is_favorite,
  };
}

/**
 * Convert checklist concepts (grouped by dimension) to selectedConceptIds
 * by matching dimension + value name to conceptsList.
 */
export function conceptsGroupedToSelectedIds(
  grouped: { title: string; values: string[] }[],
  conceptsList: ChecklistConceptItem[]
): string[] {
  const ids: string[] = [];
  for (const g of grouped) {
    for (const valueName of g.values) {
      const c = conceptsList.find(
        (x) => x.dimension === g.title && x.name === valueName
      );
      if (c) ids.push(c.id);
    }
  }
  return [...new Set(ids)];
}
